import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Plus, X, MapPin, Users, Trash2, Calendar as CalendarIcon, List, Clock, Repeat } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import EmailChipInput from "./EmailChipInput";
import cloudflareApi, { CalendarEvent as CloudflareCalendarEvent } from "../lib/cloudflare-api";

const fastSpring = { type: "spring" as const, stiffness: 500, damping: 30 };

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
  location?: string;
  attendees?: string[];
  color: string;
  all_day: boolean;
  reminder_minutes?: number;
  recurrence_rule?: string;
  recurrence_end?: string;
  created_at: string;
}

type RecurrenceType = 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';

const RECURRENCE_OPTIONS: { value: RecurrenceType; label: string; rule: string | null }[] = [
  { value: 'none', label: 'No repetir', rule: null },
  { value: 'daily', label: 'Diariamente', rule: 'FREQ=DAILY' },
  { value: 'weekly', label: 'Semanalmente', rule: 'FREQ=WEEKLY' },
  { value: 'biweekly', label: 'Cada 2 semanas', rule: 'FREQ=WEEKLY;INTERVAL=2' },
  { value: 'monthly', label: 'Mensualmente', rule: 'FREQ=MONTHLY' },
  { value: 'yearly', label: 'Anualmente', rule: 'FREQ=YEARLY' },
];

interface CalendarViewProps {
  onClose: () => void;
}

type ViewMode = 'month' | 'week' | 'list';

const PRESET_COLORS = [
  { color: '#8b5cf6', name: 'Morado', category: 'Personal' },
  { color: '#3b82f6', name: 'Azul', category: 'Trabajo' },
  { color: '#10b981', name: 'Verde', category: 'Salud' },
  { color: '#f59e0b', name: 'Naranja', category: 'Urgente' },
  { color: '#ef4444', name: 'Rojo', category: 'Importante' },
  { color: '#ec4899', name: 'Rosa', category: 'Social' },
  { color: '#6366f1', name: 'Índigo', category: 'Estudio' },
  { color: '#14b8a6', name: 'Turquesa', category: 'Deporte' },
];

export default function CalendarView({ onClose }: CalendarViewProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('month');

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [attendees, setAttendees] = useState<string[]>([]);
  const [color, setColor] = useState("#8b5cf6");
  const [allDay, setAllDay] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState(15);
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [validationError, setValidationError] = useState("");
  const [sendInvitations, setSendInvitations] = useState(false);

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const transformEvent = (e: CloudflareCalendarEvent): CalendarEvent => {
    return {
      id: e.id,
      title: e.title,
      description: e.description,
      start_time: new Date(e.start_time).toISOString().slice(0, 16),
      end_time: new Date(e.end_time).toISOString().slice(0, 16),
      location: e.location || undefined,
      attendees: e.attendees || undefined,
      color: e.color,
      all_day: e.all_day,
      reminder_minutes: e.reminder_minutes || undefined,
      recurrence_rule: e.recurrence_rule || undefined,
      recurrence_end: e.recurrence_end ? new Date(e.recurrence_end).toISOString().slice(0, 10) : undefined,
      created_at: new Date(e.created_at).toISOString(),
    };
  };

  const getRecurrenceTypeFromRule = (rule: string | undefined): RecurrenceType => {
    if (!rule) return 'none';
    const option = RECURRENCE_OPTIONS.find(opt => opt.rule === rule);
    return option?.value || 'none';
  };

  const loadEvents = async () => {
    try {
      const result = await cloudflareApi.listCalendarEvents({});
      setEvents(result.events.map(transformEvent));
    } catch (error) {
      console.error("Error loading events:", error);
    }
  };

  const validateEventDates = (): boolean => {
    if (!title.trim()) {
      setValidationError("El título es obligatorio");
      return false;
    }

    if (!startTime || !endTime) {
      setValidationError("Las fechas de inicio y fin son obligatorias");
      return false;
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    if (end <= start) {
      setValidationError("La fecha de fin debe ser posterior a la fecha de inicio");
      return false;
    }

    setValidationError("");
    return true;
  };

  const calculateDuration = (): string => {
    if (!startTime || !endTime) return "";

    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      return `${diffDays} día${diffDays > 1 ? 's' : ''}${remainingHours > 0 ? ` y ${remainingHours}h` : ''}`;
    } else if (diffHours > 0) {
      const remainingMins = diffMins % 60;
      return `${diffHours}h${remainingMins > 0 ? ` ${remainingMins}m` : ''}`;
    } else {
      return `${diffMins} minutos`;
    }
  };

  const handleCreateEvent = async () => {
    if (!validateEventDates()) return;

    try {
      const attendeesList = attendees.length > 0 ? attendees : null;
      const recurrenceRule = RECURRENCE_OPTIONS.find(opt => opt.value === recurrenceType)?.rule || null;

      const result = await cloudflareApi.createCalendarEvent({
        title,
        description,
        start_time: new Date(startTime).getTime(),
        end_time: new Date(endTime).getTime(),
        location: location || null,
        attendees: attendeesList,
        color,
        all_day: allDay,
        reminder_minutes: reminderMinutes || null,
        recurrence_rule: recurrenceRule,
        recurrence_end: recurrenceEndDate ? new Date(recurrenceEndDate).getTime() : null,
        recurrence_parent_id: null,
        recurrence_exception_dates: null,
        send_invitations: sendInvitations && attendeesList !== null,
      });

      if (result.invitations_sent) {
        console.log('📧 Invitaciones enviadas exitosamente');
      } else if (result.invitation_error) {
        console.warn('⚠️ No se pudieron enviar invitaciones:', result.invitation_error);
      }

      resetForm();
      setShowEventModal(false);
      loadEvents();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    if (!validateEventDates()) return;

    try {
      const recurrenceRule = RECURRENCE_OPTIONS.find(opt => opt.value === recurrenceType)?.rule || null;

      await cloudflareApi.updateCalendarEvent(selectedEvent.id, {
        title,
        description,
        start_time: new Date(startTime).getTime(),
        end_time: new Date(endTime).getTime(),
        location: location || null,
        attendees: attendees.length > 0 ? attendees : null,
        color,
        all_day: allDay,
        reminder_minutes: reminderMinutes || null,
        recurrence_rule: recurrenceRule,
        recurrence_end: recurrenceEndDate ? new Date(recurrenceEndDate).getTime() : null,
      });

      resetForm();
      setShowEventModal(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm("¿Eliminar este evento?")) return;

    try {
      await cloudflareApi.deleteCalendarEvent(eventId);
      loadEvents();
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setStartTime("");
    setEndTime("");
    setLocation("");
    setAttendees([]);
    setColor("#8b5cf6");
    setAllDay(false);
    setReminderMinutes(15);
    setRecurrenceType('none');
    setRecurrenceEndDate("");
    setIsCreating(false);
    setValidationError("");
    setSendInvitations(false);
  };

  const openCreateModal = (prefilledDate?: Date) => {
    resetForm();

    if (prefilledDate) {
      const year = prefilledDate.getFullYear();
      const month = String(prefilledDate.getMonth() + 1).padStart(2, '0');
      const day = String(prefilledDate.getDate()).padStart(2, '0');
      const startDateTime = `${year}-${month}-${day}T09:00`;
      const endDateTime = `${year}-${month}-${day}T10:00`;

      setStartTime(startDateTime);
      setEndTime(endDateTime);
    }

    setIsCreating(true);
    setShowEventModal(true);
  };

  const handleDayClick = (day: Date) => {
    openCreateModal(day);
  };

  const handleDayDoubleClick = async (day: Date) => {
    const startDate = new Date(day);
    startDate.setHours(9, 0, 0, 0);
    const endDate = new Date(day);
    endDate.setHours(10, 0, 0, 0);

    try {
      await cloudflareApi.createCalendarEvent({
        title: "Nuevo evento",
        description: "",
        start_time: startDate.getTime(),
        end_time: endDate.getTime(),
        location: null,
        attendees: null,
        color: "#8b5cf6",
        all_day: false,
        reminder_minutes: 15,
        recurrence_rule: null,
        recurrence_end: null,
        recurrence_parent_id: null,
        recurrence_exception_dates: null,
      });
      loadEvents();
    } catch (error) {
      console.error("Error creating quick event:", error);
    }
  };

  const openEditModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setTitle(event.title);
    setDescription(event.description);
    setStartTime(event.start_time);
    setEndTime(event.end_time);
    setLocation(event.location || "");
    setAttendees(event.attendees || []);
    setColor(event.color);
    setAllDay(event.all_day);
    setReminderMinutes(event.reminder_minutes || 15);
    setRecurrenceType(getRecurrenceTypeFromRule(event.recurrence_rule));
    setRecurrenceEndDate(event.recurrence_end || "");
    setIsCreating(false);
    setShowEventModal(true);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getEventsForDay = (date: Date | null) => {
    if (!date) return [];

    return events.filter(event => {
      const eventStart = new Date(event.start_time);
      const eventEnd = new Date(event.end_time);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return (
        (eventStart >= dayStart && eventStart <= dayEnd) ||
        (eventEnd >= dayStart && eventEnd <= dayEnd) ||
        (eventStart < dayStart && eventEnd > dayEnd)
      );
    });
  };

  const isEventContinuing = (event: CalendarEvent, date: Date): 'start' | 'middle' | 'end' | 'single' => {
    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    const startsToday = eventStart >= dayStart && eventStart <= dayEnd;
    const endsToday = eventEnd >= dayStart && eventEnd <= dayEnd;

    if (startsToday && endsToday) return 'single';
    if (startsToday) return 'start';
    if (endsToday) return 'end';
    return 'middle';
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    return events
      .filter(event => new Date(event.start_time) >= now)
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  };

  const groupEventsByDate = () => {
    const grouped: { [key: string]: CalendarEvent[] } = {};
    const upcoming = getUpcomingEvents();

    upcoming.forEach(event => {
      const dateKey = new Date(event.start_time).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });

    return grouped;
  };

  const getRelativeDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    if (dateOnly.getTime() === today.getTime()) return 'Hoy';
    if (dateOnly.getTime() === tomorrow.getTime()) return 'Mañana';

    const diffDays = Math.floor((dateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) return date.toLocaleDateString('es-ES', { weekday: 'long' });

    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getEventsForWeekHour = (day: Date, hour: number) => {
    return events.filter(event => {
      const eventStart = new Date(event.start_time);
      const eventEnd = new Date(event.end_time);

      const hourStart = new Date(day);
      hourStart.setHours(hour, 0, 0, 0);
      const hourEnd = new Date(day);
      hourEnd.setHours(hour, 59, 59, 999);

      return (
        (eventStart >= hourStart && eventStart <= hourEnd) ||
        (eventEnd >= hourStart && eventEnd <= hourEnd) ||
        (eventStart < hourStart && eventEnd > hourEnd)
      );
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const previousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const today = () => {
    setCurrentDate(new Date());
  };

  useEffect(() => {
    if (allDay && startTime && endTime) {
      const start = new Date(startTime);
      const end = new Date(endTime);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      const formatDateTime = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setStartTime(formatDateTime(start));
      setEndTime(formatDateTime(end));
    }
  }, [allDay]);

  const monthName = currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth(currentDate);
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const weekDaysForWeek = getWeekDays();
  const groupedEvents = groupEventsByDate();

  const renderMonthView = () => (
    <div
      key={`${currentDate.getFullYear()}-${currentDate.getMonth()}`}
      className="calendar-grid"
    >
      <div className="calendar-weekdays">
        {weekDays.map(day => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}
      </div>
      <div className="calendar-days">
        {days.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isToday = day && day.toDateString() === new Date().toDateString();

          return (
            <div
              key={index}
              className={`calendar-day${!day ? ' empty' : ''}${isToday ? ' today' : ''}`}
              onClick={() => day && handleDayClick(day)}
              onDoubleClick={() => day && handleDayDoubleClick(day)}
              style={{ transition: 'background-color 150ms ease, transform 150ms ease' }}
            >
              {day && (
                <>
                  <div className="calendar-day-number">{day.getDate()}</div>
                  <div className="calendar-day-events">
                    {dayEvents.slice(0, 3).map(event => {
                      const position = isEventContinuing(event, day);
                      return (
                        <div
                          key={event.id}
                          className={`calendar-event-chip event-${position}`}
                          style={{
                            backgroundColor: event.color,
                            transition: 'transform 150ms ease',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(event);
                          }}
                        >
                          <span className="calendar-event-time">
                            {!event.all_day && position === 'start' && formatTime(event.start_time)}
                          </span>
                          <span className="calendar-event-title">{event.title}</span>
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="calendar-event-more">
                        +{dayEvents.length - 3} más
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderWeekView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="calendar-week-view">
        <div className="calendar-week-header">
          <div className="calendar-week-header-spacer"></div>
          {weekDaysForWeek.map((day, index) => {
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <div
                key={index}
                className={`calendar-week-day-header${isToday ? ' today' : ''}`}
              >
                <div className="week-day-name">{weekDays[day.getDay()]}</div>
                <div className="week-day-number">{day.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="calendar-week-grid">
          <div className="calendar-week-hours">
            {hours.map(hour => (
              <div key={hour} className="calendar-week-hour-label">
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {weekDaysForWeek.map((day, dayIndex) => (
            <div key={dayIndex} className="calendar-week-day-column">
              {hours.map(hour => {
                const hourEvents = getEventsForWeekHour(day, hour);
                return (
                  <div
                    key={hour}
                    className="calendar-week-hour-cell"
                    onClick={() => {
                      const clickedDate = new Date(day);
                      clickedDate.setHours(hour, 0, 0, 0);
                      openCreateModal(clickedDate);
                    }}
                  >
                    {hourEvents.map(event => (
                      <div
                        key={event.id}
                        className="calendar-week-event"
                        style={{
                          backgroundColor: event.color,
                          transition: 'transform 150ms ease',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(event);
                        }}
                      >
                        <div className="week-event-title">{event.title}</div>
                        <div className="week-event-time">{formatTime(event.start_time)}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderListView = () => (
    <div className="calendar-list-view">
      {Object.keys(groupedEvents).length === 0 ? (
        <div className="calendar-list-empty">
          <CalendarIcon size={48} opacity={0.3} />
          <p>No hay eventos próximos</p>
        </div>
      ) : (
        Object.entries(groupedEvents).map(([dateKey, dateEvents]) => (
          <div
            key={dateKey}
            className="calendar-list-date-group"
          >
            <div className="calendar-list-date-header">
              <Clock size={16} />
              <span>{getRelativeDate(dateKey)}</span>
              <span className="calendar-list-date-full">
                {new Date(dateKey).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            <div className="calendar-list-events">
              {dateEvents.map((event) => (
                <div
                  key={event.id}
                  className="calendar-list-event"
                  style={{ transition: 'background-color 150ms ease, transform 150ms ease', cursor: 'pointer' }}
                  onClick={() => openEditModal(event)}
                >
                  <div className="calendar-list-event-color" style={{ backgroundColor: event.color }} />
                  <div className="calendar-list-event-content">
                    <div className="calendar-list-event-title">{event.title}</div>
                    <div className="calendar-list-event-details">
                      <span className="calendar-list-event-time">
                        {event.all_day ? 'Todo el día' : `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`}
                      </span>
                      {event.location && (
                        <>
                          <span className="calendar-list-separator">•</span>
                          <span className="calendar-list-event-location">
                            <MapPin size={12} />
                            {event.location}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div
      className="calendar-view"
      style={{ opacity: 0, animation: 'calFadeIn 200ms ease forwards' }}
    >
      <div className="calendar-header">
        <div className="calendar-header-left">
          <button
            onClick={onClose}
            className="calendar-back-btn"
            style={{ transition: 'transform 150ms ease' }}
          >
            <ChevronLeft size={20} />
          </button>
          <h2>Calendario</h2>
        </div>
        <div className="calendar-header-center">
          <div className="calendar-view-switcher">
            <button
              className={viewMode === 'month' ? 'active' : ''}
              onClick={() => setViewMode('month')}
              title="Vista mensual"
            >
              <CalendarIcon size={16} />
              Mes
            </button>
            <button
              className={viewMode === 'week' ? 'active' : ''}
              onClick={() => setViewMode('week')}
              title="Vista semanal"
            >
              <Clock size={16} />
              Semana
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <List size={16} />
              Lista
            </button>
          </div>
        </div>
        <div className="calendar-header-actions">
          <button onClick={today} className="calendar-today-btn">
            Hoy
          </button>
          <div className="calendar-nav">
            <button onClick={viewMode === 'week' ? previousWeek : previousMonth}>
              <ChevronLeft size={18} />
            </button>
            <span className="calendar-month-name">{monthName}</span>
            <button onClick={viewMode === 'week' ? nextWeek : nextMonth}>
              <ChevronRight size={18} />
            </button>
          </div>
          <button
            onClick={() => openCreateModal()}
            className="calendar-create-btn"
          >
            <Plus size={18} />
            Nuevo Evento
          </button>
        </div>
      </div>

      <div className="calendar-content">
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'list' && renderListView()}
      </div>

      {/* Event Modal */}
      <AnimatePresence>
        {showEventModal && (
          <motion.div
            className="calendar-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              className="calendar-modal"
              initial={{ opacity: 0, scale: 0.95, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 8 }}
              transition={fastSpring}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="calendar-modal-header">
                <h3>{isCreating ? 'Nuevo Evento' : 'Editar Evento'}</h3>
                <button onClick={() => setShowEventModal(false)} style={{ transition: 'transform 150ms ease' }}>
                  <X size={20} />
                </button>
              </div>
              <div className="calendar-modal-content">
                <div className="calendar-form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Título del evento"
                  />
                </div>

                <div className="calendar-form-group">
                  <label>Descripción</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descripción del evento"
                    rows={3}
                  />
                </div>

                <div className="calendar-form-row">
                  <div className="calendar-form-group">
                    <label>Inicio *</label>
                    <input
                      type="datetime-local"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div className="calendar-form-group">
                    <label>Fin *</label>
                    <input
                      type="datetime-local"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>

                <div className="calendar-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={allDay}
                      onChange={(e) => setAllDay(e.target.checked)}
                    />
                    Todo el día
                  </label>
                </div>

                <div className="calendar-form-group">
                  <label>
                    <MapPin size={16} />
                    Ubicación
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Ubicación del evento"
                  />
                </div>

                <div className="calendar-form-group">
                  <label>
                    <Users size={16} />
                    Asistentes
                  </label>
                  <EmailChipInput
                    value={attendees}
                    onChange={setAttendees}
                    placeholder="email1@ejemplo.com, email2@ejemplo.com"
                  />
                </div>

                {isCreating && attendees.length > 0 && (
                  <div className="calendar-form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={sendInvitations}
                        onChange={(e) => setSendInvitations(e.target.checked)}
                      />
                      Enviar invitaciones por correo (.ics)
                    </label>
                  </div>
                )}

                {validationError && (
                  <div className="calendar-validation-error">
                    {validationError}
                  </div>
                )}

                {startTime && endTime && !validationError && (
                  <div className="calendar-duration-info">
                    <Clock size={14} />
                    Duración: {calculateDuration()}
                  </div>
                )}

                <div className="calendar-form-group">
                  <label>Color y Categoría</label>
                  <div className="calendar-color-presets">
                    {PRESET_COLORS.map((preset) => (
                      <button
                        key={preset.color}
                        type="button"
                        className={`calendar-color-preset${color === preset.color ? ' active' : ''}`}
                        style={{ backgroundColor: preset.color, transition: 'transform 150ms ease' }}
                        onClick={() => setColor(preset.color)}
                        title={preset.category}
                      >
                        {color === preset.color && '\u2713'}
                      </button>
                    ))}
                  </div>
                  <div className="calendar-color-categories">
                    {PRESET_COLORS.map((preset) => (
                      <span
                        key={preset.color}
                        className={`calendar-color-category${color === preset.color ? ' active' : ''}`}
                        onClick={() => setColor(preset.color)}
                        style={{ transition: 'transform 150ms ease', cursor: 'pointer' }}
                      >
                        {preset.category}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="calendar-form-group">
                  <label>Recordatorio</label>
                  <select
                    value={reminderMinutes}
                    onChange={(e) => setReminderMinutes(parseInt(e.target.value))}
                  >
                    <option value={0}>Sin recordatorio</option>
                    <option value={5}>5 minutos antes</option>
                    <option value={15}>15 minutos antes</option>
                    <option value={30}>30 minutos antes</option>
                    <option value={60}>1 hora antes</option>
                    <option value={1440}>1 día antes</option>
                  </select>
                </div>

                <div className="calendar-form-group">
                  <label>
                    <Repeat size={16} />
                    Repetición
                  </label>
                  <select
                    value={recurrenceType}
                    onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
                  >
                    {RECURRENCE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {recurrenceType !== 'none' && (
                  <div className="calendar-form-group">
                    <label>Repetir hasta (opcional)</label>
                    <input
                      type="date"
                      value={recurrenceEndDate}
                      onChange={(e) => setRecurrenceEndDate(e.target.value)}
                      min={startTime ? startTime.split('T')[0] : undefined}
                    />
                    <span className="calendar-form-hint">
                      Dejar vacío para repetir indefinidamente
                    </span>
                  </div>
                )}
              </div>
              <div className="calendar-modal-footer">
                {!isCreating && selectedEvent && (
                  <button
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                    className="calendar-btn-danger"
                    style={{ transition: 'transform 150ms ease, background-color 150ms ease' }}
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </button>
                )}
                <div className="calendar-modal-actions">
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="calendar-btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={isCreating ? handleCreateEvent : handleUpdateEvent}
                    className="calendar-btn-primary"
                  >
                    {isCreating ? 'Crear' : 'Guardar'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes calFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
